import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Camera, Loader2, Utensils, Sparkles, Dumbbell, MapPin } from 'lucide-react';
import { InteractiveMealCard } from '@/components/InteractiveMealCard';
import ScreenBackground from '@/components/ScreenBackground';
import { toast } from 'sonner';
import axios from 'axios';
import logger from '@/utils/logger';

const API = '/api';

export const WhatToEatModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiMeals, setAiMeals] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [photoSuggestions, setPhotoSuggestions] = useState(null);
  const [photoAnalyzing, setPhotoAnalyzing] = useState(false);
  const [showNearby, setShowNearby] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchSuggestions();
      setAiMeals(null);
      setPhotoSuggestions(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/suggestions/what-to-eat?client_hour=${new Date().getHours()}`, authHeaders());
      setData(res.data);
    } catch (err) {
      logger.error(err);
    }
    setLoading(false);
  };

  const handleBuildMeal = async () => {
    if (!data) return;
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/suggestions/build-meal`, {
        remaining_cal: data.remaining.calories,
        remaining_protein: data.remaining.protein,
        remaining_carbs: data.remaining.carbs,
        remaining_fat: data.remaining.fat,
      }, authHeaders());
      setAiMeals(res.data.meals || []);
      if (res.data.error) toast.error(res.data.error);
    } catch (err) {
      toast.error('Failed to generate meal ideas');
    }
    setAiLoading(false);
  };

  const handlePhotoScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoAnalyzing(true);
    setPhotoSuggestions(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const scanRes = await axios.post(`${API}/fridge-scan/analyze`, formData, {
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      const ingredients = scanRes.data.ingredients || [];
      if (ingredients.length === 0) {
        toast.error("Couldn't identify any ingredients. Try a clearer photo.");
        setPhotoAnalyzing(false);
        return;
      }
      const suggestRes = await axios.post(`${API}/fridge-scan/suggest-meals`, {
        scan_id: scanRes.data.scan_id,
      }, authHeaders());
      setPhotoSuggestions({
        ingredients,
        meals: suggestRes.data.suggestions || [],
      });
    } catch {
      toast.error('Failed to analyze photo. Try again.');
    }
    setPhotoAnalyzing(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[55]" data-testid="what-to-eat-modal" style={{ paddingBottom: 0 }}>
      <ScreenBackground screen="what_to_eat" className="h-full overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Header */}
        <header className="sticky top-0 z-50 border-b px-4 py-4 glass-header">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <button data-testid="close-what-to-eat" onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
              <ArrowLeft className="w-5 h-5 text-[#1A3A4A] dark:text-[#F0EDE8]" />
            </button>
            <h1 className="text-lg font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">What Should I Eat?</h1>
          </div>
        </header>

        <main className="px-4 pb-32 pt-6 max-w-lg mx-auto">
          {/* Intro */}
          <div className="mb-5">
            <p className="text-sm text-[#1A3A4A] dark:text-[#F0EDE8] leading-relaxed">
              Personalized meals based on your macro gaps, history, and taste preferences. Tap <strong>Log This</strong> to instantly add to your day.
            </p>
          </div>

          {/* Action bar: Photo scan + AI build */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="rounded-[16px] border bg-white border-[#E0DCD4] dark:bg-[#1A2530] dark:border-white/10 p-3">
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoScan} />
              <button
                data-testid="photo-what-can-i-make"
                onClick={() => fileRef.current?.click()}
                disabled={photoAnalyzing}
                className="w-full flex flex-col items-center gap-2 py-2 hover:opacity-80 transition-opacity disabled:opacity-50"
              >
                <div className="w-10 h-10 rounded-xl bg-[#4A90D9]/15 flex items-center justify-center">
                  {photoAnalyzing ? <Loader2 className="w-5 h-5 text-[#4A90D9] animate-spin" /> : <Camera className="w-5 h-5 text-[#4A90D9]" />}
                </div>
                <div className="text-center">
                  <p className="text-xs font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">
                    {photoAnalyzing ? 'Scanning...' : 'Scan My Fridge'}
                  </p>
                  <p className="text-[10px] text-[#7A7A72] dark:text-[#A1A1AA]">Photo of what you have</p>
                </div>
              </button>
            </div>

            <div className="rounded-[16px] border bg-white border-[#E0DCD4] dark:bg-[#1A2530] dark:border-white/10 p-3">
              <button
                data-testid="build-me-a-meal-btn"
                onClick={handleBuildMeal}
                disabled={aiLoading || !data}
                className="w-full flex flex-col items-center gap-2 py-2 hover:opacity-80 transition-opacity disabled:opacity-50"
              >
                <div className="w-10 h-10 rounded-xl bg-[#6B9E8A]/15 flex items-center justify-center">
                  {aiLoading ? <Loader2 className="w-5 h-5 text-[#6B9E8A] animate-spin" /> : <Sparkles className="w-5 h-5 text-[#6B9E8A]" />}
                </div>
                <div className="text-center">
                  <p className="text-xs font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">
                    {aiLoading ? 'Building...' : 'Build Me a Meal'}
                  </p>
                  <p className="text-[10px] text-[#7A7A72] dark:text-[#A1A1AA]">AI-powered ideas</p>
                </div>
              </button>
            </div>
          </div>

          {/* Photo-based suggestions */}
          {photoSuggestions && (
            <div className="mb-6 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-sm font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">Based on what you have</h2>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#4A90D9]/10 text-[#4A90D9]">
                  {photoSuggestions.ingredients.length} items found
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {photoSuggestions.ingredients.slice(0, 10).map((ing, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-white/10 dark:bg-white/5 text-[#7A7A72] dark:text-[#A1A1AA] border border-[#1A3A4A]/5 dark:border-white/10">
                    {ing.name || ing}
                  </span>
                ))}
              </div>
              {photoSuggestions.meals.map((meal, i) => (
                <InteractiveMealCard
                  key={`photo-${i}`}
                  name={meal.name}
                  cal={meal.est_calories || meal.calories}
                  protein={meal.est_protein || meal.protein}
                  carbs={meal.est_carbs || meal.carbs}
                  fat={meal.est_fat || meal.fat}
                  items={meal.description || meal.items}
                  source={meal.recipe_id ? "recipe_db" : "photo_scan"}
                  sourceNote="From your photo"
                  linkedRecipeId={meal.recipe_id}
                />
              ))}
              {photoSuggestions.meals.length === 0 && (
                <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA] text-center py-4">No meal ideas found. Try scanning more items.</p>
              )}
            </div>
          )}

          {/* AI-generated meals */}
          {aiMeals && aiMeals.length > 0 && (
            <div className="mb-6 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-[#6B9E8A]" />
                <h2 className="text-sm font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">From Your Recipe Library</h2>
              </div>
              {aiMeals.map((meal, i) => (
                <InteractiveMealCard
                  key={`ai-${i}`}
                  name={meal.name}
                  cal={meal.calories}
                  protein={meal.protein}
                  carbs={meal.carbs}
                  fat={meal.fat}
                  items={meal.items}
                  cook_time={meal.cook_time}
                  source="recipe_db"
                  linkedRecipeId={meal.recipe_id}
                />
              ))}
            </div>
          )}

          {/* Regular suggestions */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-[#6B9E8A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : data ? (
            <>
              {/* Remaining budget */}
              <div className="rounded-[16px] border bg-white border-[#E0DCD4] dark:bg-[#1A2530] dark:border-white/10 p-6 text-center mb-5">
                <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA] mb-2">You still need about</p>
                <div className="text-4xl font-bold text-[#1A3A4A] dark:text-[#F0EDE8]">
                  {data.remaining.calories} cal
                </div>
                <div className="text-lg font-semibold text-[#4A90D9] mt-1">
                  {data.remaining.protein}g protein
                </div>
                {data.post_workout && data.recent_workout && (
                  <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-[#C67A5C]">
                    <Dumbbell className="w-3.5 h-3.5" />
                    <span>Post-workout: {data.recent_workout.type} ({data.recent_workout.calories} cal burned)</span>
                  </div>
                )}
                {data.is_over_target && (
                  <p className="text-sm text-[#C67A5C] mt-3">You're over target, but if you're hungry...</p>
                )}
              </div>

              {/* Suggestion cards as interactive meal cards */}
              {data.suggestions.length > 0 && (
                <div className="space-y-3 mb-6">
                  <h2 className="text-sm font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">
                    Personalized for You
                  </h2>
                  {data.suggestions.map((s, i) => (
                    <InteractiveMealCard
                      key={`sug-${i}`}
                      name={s.name}
                      cal={s.calories}
                      protein={s.protein}
                      carbs={s.carbs}
                      fat={s.fat}
                      source={s.source || "recipe_db"}
                      sourceNote={s.source_note}
                      tasteRating={s.taste_rating}
                      postWorkout={s.post_workout}
                      linkedRecipeId={s.recipe_id}
                    />
                  ))}
                </div>
              )}

              {data.suggestions.length === 0 && !photoSuggestions && !aiMeals && (
                <div className="rounded-[16px] border bg-white border-[#E0DCD4] dark:bg-[#1A2530] dark:border-white/10 p-8 text-center">
                  <Utensils className="w-10 h-10 text-[#6B9E8A] mx-auto mb-4" />
                  <h3 className="text-base font-semibold text-[#1A3A4A] dark:text-[#F0EDE8] mb-2">No saved meals yet</h3>
                  <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA] leading-relaxed max-w-xs mx-auto mb-4">
                    Tap "Build Me a Meal" above for personalized recipe suggestions, or start logging meals to get recommendations.
                  </p>
                </div>
              )}

              {/* "Don't feel like cooking?" — Navigate to Restaurant Hub */}
              {(data.suggestions.length > 0 || aiMeals?.length > 0 || photoSuggestions?.meals?.length > 0) && (
                <div className="mb-6">
                  <button
                    onClick={() => { onClose(); navigate('/restaurants'); }}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-[#C67A5C] bg-[#C67A5C]/8 hover:bg-[#C67A5C]/15 transition-colors border border-[#C67A5C]/15"
                    data-testid="dont-feel-like-cooking-btn"
                  >
                    <MapPin className="w-4 h-4" />
                    Don't feel like cooking? Find something nearby
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-[16px] border bg-white border-[#E0DCD4] dark:bg-[#1A2530] dark:border-white/10 p-8 text-center">
              <Utensils className="w-10 h-10 text-[#6B9E8A] mx-auto mb-4" />
              <h3 className="text-base font-semibold text-[#1A3A4A] dark:text-[#F0EDE8] mb-2">Something went wrong</h3>
              <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA]">Pull down to refresh or try again later.</p>
            </div>
          )}
        </main>
      </ScreenBackground>
    </div>
  );
};
